/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faCopy } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';

// actions
import { getPermissions } from '../../../../actions/permissions';

// components
import { Table } from '../../../../common/components';
import { showPopup } from '../../../../utils/popup';

function EditButton({ permission = {} }) {
  const dispatch = useDispatch();

  function handleEditInfo() {
    dispatch({ type: 'SET_CARRIER_PERMISSIONS_PAGE', page: 'addEdit' });
    dispatch({ type: 'SET_EDIT_PERMISSIONS', editing: true, level: 'carrier', payload: permission });
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0 text-sky font-weight-bold"
      onClick={() => handleEditInfo()}
    >
      Edit
    </button>
  );
}

function Dropdown({ data }) {
  const { rts: rtsFeature } = useSelector(store => store.theme.features);
  const [ showNotification, setShowNotification ] = useState(false);

  // cell formatting
  const formatStatesCell = (states) => states.join(', ');
  const formatDateTimeCell = (date) => moment(date).format('MM/DD/YYYY hh:mm A');
  const formatDateCell = (date) => moment(date).format('MM/DD/YYYY');
  const formatUpdatedByCell = (data) => {
    if (!data || !data.length) return 'Automated';
    const updatedBy = data[0].log.updated_by;
    return updatedBy ? `${updatedBy.first_name} ${updatedBy.last_name} | ${updatedBy.email}` : 'Automated';
  }

  // table schemas
  const additionalInfoSchema = {
    cols: [
      { header: 'Last Updated', field: 'updated', formatter: formatDateTimeCell },
      { header: 'Active Date', field: 'active_date', formatter: formatDateCell },
      { header: 'States', field: 'states', formatter: formatStatesCell },
    ],
  };
  const rtsSchema = {
    cols: [
      { header: 'Last Updated', field: 'updated', formatter: formatDateTimeCell },
      { header: 'File Family ID', field: 'file_family_id' },
      { header: 'File Source', field: 'file_source' },
      { header: 'User Who Made the Change', field: 'change_history', formatter: formatUpdatedByCell },
    ],
  };
  const rtsChangelogSchema = {
    cols: [
      { header: 'Change Date', field: 'change_date', formatter: formatDateTimeCell },
      { header: 'Description', field: 'description' },
      { header: 'File Path', field: 'rts_file_path' },
      { header: 'File Name', field: 'rts_file_name' },
      { header: 'CP Status', field: 'cp_status' },
    ],
  };

  useEffect(() => {
    setTimeout(() => setShowNotification(false), 5000);
  }, [showNotification]);

  return (
    <div>
      {rtsFeature && (<h5 className="mt-2 align-middle">Additional Info</h5>)}
      <Table
        style={{ backgroundColor: 'rgba(var(--bs-secondary-rgb), 0.13)' }}
        className="table table-bordered mb-0"
        schema={additionalInfoSchema}
        data={[data]}
      />
      {rtsFeature && (
        <>
          <h5 className="mt-2 align-middle">
            RTS Info
          </h5>
          <Table
            style={{ backgroundColor: 'rgba(var(--bs-secondary-rgb), 0.13)' }}
            className="table table-bordered mb-0"
            schema={rtsSchema}
            data={[data]}
          />
          <h5 className="mt-2 align-middle">
            RTS Changelog
            <button
              className="ms-3 btn btn-sm btn-outline-secondary"
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(data.change_history));
                setShowNotification(true);
              }}
            >
              <FontAwesomeIcon size="sm" icon={faCopy} className="me-1" />
              Copy JSON to clipboard
            </button>
            <span className={`ms-3 text-success fs-5 fw-bold fade ${showNotification ? 'show' : ''}`}>
              Copied!
            </span>
          </h5>
          <Table
            style={{ backgroundColor: 'rgba(var(--bs-secondary-rgb), 0.13)' }}
            className="table table-bordered mb-0"
            schema={rtsChangelogSchema}
            data={data.change_history || []}
          />
        </>
      )}
    </div>
  );
}

export default function CarrierTable() {
  const dispatch = useDispatch();
  const { records: data = [] } = useSelector(store => store.permissions.carrier);
  const {
    carriers,
    user: profileUser = {},
    theme: { features: { rts: rtsFeature } },
    agents: { selectedAgent = { } },
  } = useSelector(store => store);
  const { username: agentUsername } = selectedAgent;
  const { username: profileUsername } = profileUser;

  const [ planYearFilter, setPlanYearFilter ] = useState();
  const [ loading, setLoading ] = useState(false);
  const [ filteredData, setFilteredData ] = useState([]);
  const [ initialLoad, setInitialLoad ] = useState(false);

  // cell formatting
  const formatEditCell = (p) => <EditButton permission={p} />;
  const formatCarrierCell = (cid) => carriers[cid];
  const formatActiveCell = (active) => (active
    ? <FontAwesomeIcon className="text-success" icon={faCheck} />
    : <FontAwesomeIcon className="text-danger" icon={faTimes} />
  );
  const cellCommonClasses = 'fs-6 badge rounded-pill';
  const formatProductsCell = (cp) => (
    <>
      <span className={`${cellCommonClasses} ${cp.product_mapd ? 'text-bg-success' : 'text-bg-danger'}`}>
        MAPD
      </span>
      <span className={`ms-1 ${cellCommonClasses} ${cp.product_ma ? 'text-bg-success' : 'text-bg-danger'}`}>
        MA
      </span>
      <span className={`ms-1 ${cellCommonClasses} ${cp.product_pdp ? 'text-bg-success' : 'text-bg-danger'}`}>
        PDP
      </span>
      <span className={`ms-1 ${cellCommonClasses} ${cp.product_medigap ? 'text-bg-success' : 'text-bg-danger'}`}>
        Medigap
      </span>
    </>
  );

  useEffect(() => {
    setLoading(true);
    getPermissions(agentUsername || profileUsername, 'carrier')
      .finally(() => setLoading(false));
  }, []);

  const planYears = [ ...new Set(data.map(p => p.plan_year)) ].sort((a, b) => (b - a)) // year highest to lowest

  useEffect(() => {
    if (!initialLoad && planYears.length) {
      setPlanYearFilter(planYears[0]);
      setInitialLoad(true);
    }
  }, [planYears]);

  useEffect(() => {
    try {
      if (Array.isArray(carriers)) {
        setFilteredData(data
          .filter(p => (planYearFilter ? String(p.plan_year) === String(planYearFilter) : true))
          .sort((a, b) => {
            if (!carriers[a.carrier_id] || !carriers[b.carrier_id]) {
              const missingCarrierID = !carriers[a.carrier_id] ? a.carrier_id : b.carrier_id;
              const errorMessage = `carrier_id ${missingCarrierID} is undefined for this Docurobut App ID`;
              console.error(errorMessage);
              showPopup('Error', errorMessage, 'error');
              return -1;
            } else {
              return carriers[a.carrier_id].localeCompare(carriers[b.carrier_id]); // sort by carrier name alpha
            }
          }) 
        );
      }
    } catch (e) {
      setFilteredData([]);
      console.error(e);
      showPopup('Error', 'There was an error loading the data.', 'error');
    } 
  }, [data, planYearFilter]);

  const tableSchema = {
    cols: [
      { header: 'Active', field: 'active', formatter: formatActiveCell },
      { header: 'Carrier', field: 'carrier_id', formatter: formatCarrierCell },
      { header: 'Plan Year', field: 'plan_year' },
      { header: 'NPN', field: 'npn' },
      { header: 'Carrier Agent ID', field: 'carrier_agent_id' },
      { header: 'Products', passAllData: true, formatter: formatProductsCell },
      { header: 'More Info', openSubComponent: true, field: 'none' },
      { header: '', formatter: formatEditCell, passAllData: true },
    ],
    subComponentContainerStyle: { backgroundColor: 'rgba(var(--bs-primary-rgb), 0.13)' },
    subComponent: (data) => <Dropdown data={data} />,
  };

  function handleCreateNewPermission() {
    dispatch({ type: 'SET_CARRIER_PERMISSIONS_PAGE', page: 'addEdit' });
    dispatch({ type: 'RESET_EDIT_PERMISSIONS_NEW_ENTRY', editing: false, level: 'carrier' });
  }

  return (
    <div className="bg-white border">
      <div className="row fs-4 m-4">
        <div className="d-flex col-auto align-items-center">
          <label htmlFor="plan-year-select">
            Plan Year
          </label>
        </div>
        <div className="col-auto">
          <select
            id="plan-year-select"
            className="form-select"
            value={planYearFilter || ''}
            onChange={(e) => setPlanYearFilter(e.target.value)}
          >
            <option value="">All</option>
            {planYears.map((p) => (
              <option value={p} key={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="align-self-end fade-in col-auto ms-auto">
          <button
            type="button"
            className="btn btn-success px-4"
            onClick={() => handleCreateNewPermission()}
          >
            <FontAwesomeIcon icon={faPlus} size="lg" className="me-2" />
            Create New Permission
          </button>
        </div>
      </div>

      <hr />

      <div className="row">
        <div className="col">
          <Table
            loading={loading}
            className="table table-striped"
            schema={tableSchema}
            data={filteredData}
          />
        </div>
      </div>
    </div>
  );
}

